import { FIREBASE_ERROR_CODES } from '@/config/firebase/utils/errors'
import { useAuthContext } from '@/features/auth/contexts/AuthContext'
import { useErrorHandler } from '@/features/errorHandling/hooks/useErrorHandler'
import { ErrorSeverity } from '@/features/errorHandling/types/errorTypes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FirestoreError } from 'firebase/firestore'
import { fetchUserProfile, updateUserProfile } from '../api/profileService'
import type { UserProfile } from '../types/ProfileTypes'

const PROFILE_CACHE_TIME = 5 * 60 * 1000
const MAX_RETRIES = 3

export const useProfile = () => {
    const { currentUser } = useAuthContext()
    const queryClient = useQueryClient()
    const { handleError, showMessage } = useErrorHandler()

    const {
        data: profile,
        isLoading,
        error,
        refetch,
    } = useQuery<UserProfile | null>({
        queryKey: ['profile', currentUser?.uid],
        queryFn: () => currentUser?.uid ? fetchUserProfile(currentUser.uid) : null,
        enabled: !!currentUser?.uid,
        retry: (failureCount, error) => {
            if (error instanceof FirestoreError && error.code === FIREBASE_ERROR_CODES.NOT_FOUND) return false

            return failureCount < MAX_RETRIES
        },
        staleTime: PROFILE_CACHE_TIME,
    })

    const {
        mutate: updateProfile,
        isPending: isUpdating,
        reset: resetMutation,
    } = useMutation({
        mutationFn: (data: UserProfile) => {
            if (!currentUser?.uid) throw new Error('User not authenticated')

            return updateUserProfile(currentUser.uid, data)
        },
        onSuccess: () => {
            showMessage('Profile updated successfully', ErrorSeverity.SUCCESS)
            queryClient.invalidateQueries({ queryKey: ['profile', currentUser?.uid] })
        },
        onError: (error) => {
            handleError(error instanceof Error ? error.message : 'Failed to update profile', ErrorSeverity.ERROR)
        },
    })

    return {
        profile,
        isLoading,
        error,
        isUpdating,
        updateProfile,
        refetchProfile: refetch,
        resetProfileMutation: resetMutation,
    }
}